/**
 * recentPatients.service.ts
 *
 * Per-user recent patients list, persisted on the server so it follows the
 * authenticated user across sessions and devices. Responses are the raw
 * entries returned by the API; the store adapts them for UI consumption.
 */
import { apiClient } from './apiClient';
import type { ApiResponse } from '@/types/api.types';

export interface RecentPatientDTO {
  patientID: number;
  name: string;
  photoUrl: string | null;
  pinned: boolean;
  pinnedAt: string | null;
  visitedAt: string;
}

function unwrap<T>(payload: ApiResponse<T>): T {
  return payload.data;
}

export const recentPatientsService = {
  list(): Promise<RecentPatientDTO[]> {
    return apiClient
      .get<ApiResponse<RecentPatientDTO[]>>('/recent-patients')
      .then((r) => unwrap(r.data) ?? []);
  },

  visit(patientID: number): Promise<void> {
    return apiClient
      .post<ApiResponse<{ success: boolean }>>('/recent-patients', { patientID })
      .then(() => undefined);
  },

  setPin(patientID: number, pinned: boolean): Promise<void> {
    return apiClient
      .patch<ApiResponse<{ success: boolean }>>(`/recent-patients/${patientID}/pin`, { pinned })
      .then(() => undefined);
  },

  remove(patientID: number): Promise<void> {
    return apiClient
      .delete<ApiResponse<{ success: boolean }>>(`/recent-patients/${patientID}`)
      .then(() => undefined);
  },

  clearNonPinned(): Promise<void> {
    return apiClient
      .delete<ApiResponse<{ success: boolean }>>('/recent-patients')
      .then(() => undefined);
  },
};
